(() => {
  if (window.ProhealthCartDelivery) return;

  const SELECTORS = {
    module: '[data-ph-delivery-module]',
    upsell: '[data-ph-cart-upsell]',
    list: '[data-ph-upsell-list]',
    card: '[data-ph-upsell-card]',
    next: '[data-ph-upsell-next]',
    track: '[data-ph-scroll-track]',
    thumb: '[data-ph-scroll-thumb]'
  };

  const state = {
    refreshTimer: null,
    cartSignature: sessionStorage.getItem('phCartSignature') || '',
    rotation: Number(sessionStorage.getItem('phUpsellRotation') || 0),
    requestId: 0,
    progressRefreshTimer: null,
    progressRefreshInFlight: null,
    /* The cards last painted. On the cart page the theme replaces the whole
       item container on every quantity change, which takes this scroller
       with it, and the replacement arrives empty because the cards are
       fetched separately. Repainting the previous cards immediately keeps
       the block at its height with content in it while the new set is
       fetched, instead of going blank and collapsing. */
    lastCardsHtml: ''
  };

  const LAST_SHOPPING_URL_KEY = 'phLastShoppingUrl';

  const isCartPath = (pathname) => /^\/cart(?:\/|$)/.test(pathname || '');

  const getSameStoreUrl = (value) => {
    if (!value) return null;
    try {
      const url = new URL(value, window.location.origin);
      return url.origin === window.location.origin ? url : null;
    } catch (error) {
      return null;
    }
  };

  const rememberShoppingLocation = () => {
    if (isCartPath(window.location.pathname)) return;
    sessionStorage.setItem(LAST_SHOPPING_URL_KEY, window.location.href);
  };

  const getContinueShoppingUrl = (fallbackUrl) => {
    const storedUrl = getSameStoreUrl(sessionStorage.getItem(LAST_SHOPPING_URL_KEY));
    if (storedUrl && !isCartPath(storedUrl.pathname)) return storedUrl.href;

    const referrerUrl = getSameStoreUrl(document.referrer);
    if (referrerUrl && !isCartPath(referrerUrl.pathname)) return referrerUrl.href;

    return getSameStoreUrl(fallbackUrl)?.href || `${window.location.origin}/collections/all`;
  };

  rememberShoppingLocation();

  const scrollToElementWhenReady = (selector, attempts = 16) => {
    const target = document.querySelector(selector);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (attempts > 0) {
      window.setTimeout(() => scrollToElementWhenReady(selector, attempts - 1), 120);
    }
  };

  const uniqueCards = (cards, cartProducts) => {
    const seen = new Set();
    return cards.filter((card) => {
      const id = card.dataset.productId;
      if (!id || seen.has(id) || cartProducts.has(id)) return false;
      seen.add(id);
      return true;
    });
  };

  const parseCards = (html) => {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    return Array.from(documentFragment.querySelectorAll(SELECTORS.card));
  };

  const recommendationUrl = (productId, intent) => {
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(`${root}recommendations/products`, window.location.origin);
    url.searchParams.set('section_id', 'prohealth-cart-recommendations');
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', '10');
    url.searchParams.set('intent', intent);
    return url.toString();
  };

  const getPool = async (module) => {
    const productIds = (module.dataset.cartProducts || '').split('|').filter(Boolean);
    if (!productIds.length) return [];

    const requests = [];
    productIds.forEach((productId) => {
      ['complementary', 'related'].forEach((intent) => {
        requests.push(
          fetch(recommendationUrl(productId, intent), {
            headers: { Accept: 'text/html' }
          }).then((response) => response.ok ? response.text() : '')
        );
      });
    });

    return (await Promise.all(requests)).flatMap(parseCards);
  };

  const updateIndicator = (module) => {
    const list = module.querySelector(SELECTORS.list);
    const thumb = module.querySelector(SELECTORS.thumb);
    if (!list || !thumb) return;

    const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth);
    const visibleRatio = list.scrollWidth ? Math.min(1, list.clientWidth / list.scrollWidth) : 1;
    const thumbWidth = Math.max(18, visibleRatio * 100);
    const travel = 100 - thumbWidth;
    const progress = maxScroll ? list.scrollLeft / maxScroll : 0;

    thumb.style.width = `${thumbWidth}%`;
    thumb.style.transform = `translateX(${progress * travel / (thumbWidth / 100)}%)`;
  };


  const bindUpsellScrollDrag = (module) => {
    const list = module.querySelector(SELECTORS.list);
    const track = module.querySelector(SELECTORS.track);
    const thumb = module.querySelector(SELECTORS.thumb);
    if (!list || !track || !thumb || track.dataset.phUpsellDragBound === 'true') return;

    track.dataset.phUpsellDragBound = 'true';

    const scrollFromPointer = (clientX) => {
      const rect = track.getBoundingClientRect();
      const thumbWidth = thumb.getBoundingClientRect().width;
      const available = Math.max(rect.width - thumbWidth, 1);
      const thumbLeft = Math.min(Math.max(clientX - rect.left - (thumbWidth / 2), 0), available);
      const maxScroll = Math.max(list.scrollWidth - list.clientWidth, 0);
      list.scrollLeft = (thumbLeft / available) * maxScroll;
    };

    const stopDragging = (event) => {
      if (track.dataset.phUpsellDragging !== 'true') return;
      track.dataset.phUpsellDragging = 'false';
      track.classList.remove('is-dragging');
      if (event?.pointerId !== undefined && track.hasPointerCapture?.(event.pointerId)) {
        track.releasePointerCapture(event.pointerId);
      }
    };

    track.addEventListener('pointerdown', (event) => {
      if (event.button !== undefined && event.button !== 0) return;
      event.preventDefault();
      track.dataset.phUpsellDragging = 'true';
      track.classList.add('is-dragging');
      track.setPointerCapture?.(event.pointerId);
      scrollFromPointer(event.clientX);
    });

    track.addEventListener('pointermove', (event) => {
      if (track.dataset.phUpsellDragging === 'true') scrollFromPointer(event.clientX);
    });
    track.addEventListener('pointerup', stopDragging);
    track.addEventListener('pointercancel', stopDragging);
  };

  const bindModule = (module) => {
    const list = module.querySelector(SELECTORS.list);
    const next = module.querySelector(SELECTORS.next);
    if (!list || !next || list.dataset.phBound === 'true') return;

    list.dataset.phBound = 'true';
    bindUpsellScrollDrag(module);
    list.addEventListener('scroll', () => updateIndicator(module), { passive: true });
    next.addEventListener('click', () => {
      const cards = list.querySelectorAll(SELECTORS.card);
      if (cards.length <= 1) return;

      const firstCard = cards[0];
      const step = firstCard.getBoundingClientRect().width + 10;
      const maxScroll = Math.max(0, list.scrollWidth - list.clientWidth);
      const isAtEnd = list.scrollLeft >= maxScroll - 4;
      const nextPosition = Math.min(list.scrollLeft + step, maxScroll);

      list.scrollTo({
        left: isAtEnd ? 0 : nextPosition,
        behavior: 'smooth'
      });
    });

    window.addEventListener('resize', () => updateIndicator(module), { passive: true });
  };

  const renderModule = async (module, requestId) => {
    const upsell = module.querySelector(SELECTORS.upsell);
    const list = module.querySelector(SELECTORS.list);
    if (!upsell || !list) return;

    upsell.classList.add('is-loading');
    bindModule(module);

    /* Paint the previous cards straight away so the block never shows as
       empty. They are replaced below, in the same frame budget as before;
       the difference is what the customer sees while waiting. Add buttons
       are bound by delegation on document, so restored markup is live. */
    if (!list.children.length && state.lastCardsHtml) {
      list.innerHTML = state.lastCardsHtml;
      upsell.classList.remove('is-empty');
    }

    try {
      const cartProducts = new Set(
        (module.dataset.cartProducts || '').split('|').filter(Boolean)
      );
      const targetAmount = Number(module.dataset.upsellTarget || module.dataset.remaining || 0);
      const pool = uniqueCards(await getPool(module), cartProducts);

      if (requestId !== state.requestId || !module.isConnected) return;

      pool.sort((a, b) => {
        const priceA = Number(a.dataset.price || 0);
        const priceB = Number(b.dataset.price || 0);
        const aMeetsGoal = targetAmount <= 0 || priceA >= targetAmount;
        const bMeetsGoal = targetAmount <= 0 || priceB >= targetAmount;

        if (aMeetsGoal !== bMeetsGoal) return aMeetsGoal ? -1 : 1;

        if (aMeetsGoal && bMeetsGoal) {
          const aboveDifference = (priceA - targetAmount) - (priceB - targetAmount);
          if (aboveDifference !== 0) return aboveDifference;
        } else {
          const belowDifference = Math.abs(priceA - targetAmount) - Math.abs(priceB - targetAmount);
          if (belowDifference !== 0) return belowDifference;
        }

        const sourceWeight = { complementary: 0, related: 1, fallback: 2 };
        return (sourceWeight[a.dataset.source] ?? 3) - (sourceWeight[b.dataset.source] ?? 3);
      });

      const configuredLimit = Number.parseInt(module.dataset.upsellLimit, 10);
      const productLimit = Number.isFinite(configuredLimit)
        ? Math.min(12, Math.max(1, configuredLimit))
        : 4;
      const required = Math.min(productLimit, pool.length);
      const offset = pool.length ? (state.rotation * productLimit) % pool.length : 0;
      const selected = [];

      for (let index = 0; index < pool.length && selected.length < required; index += 1) {
        selected.push(pool[(offset + index) % pool.length]);
      }

      list.replaceChildren(...selected);
      if (selected.length) state.lastCardsHtml = list.innerHTML;
      const next = module.querySelector(SELECTORS.next);
      if (next) next.hidden = selected.length <= 1;
      upsell.classList.toggle('is-empty', selected.length === 0);
      requestAnimationFrame(() => updateIndicator(module));
    } catch (error) {
      console.error('Prohealth cart recommendations:', error);
      upsell.classList.add('is-empty');
    } finally {
      upsell.classList.remove('is-loading');
    }
  };

  const refreshAll = async ({ rotate = false } = {}) => {
    window.clearTimeout(state.refreshTimer);
    state.refreshTimer = window.setTimeout(async () => {
      if (rotate) {
        state.rotation += 1;
        sessionStorage.setItem('phUpsellRotation', String(state.rotation));
      }

      state.requestId += 1;
      const requestId = state.requestId;
      document.querySelectorAll(SELECTORS.module).forEach((module) => {
        renderModule(module, requestId);
      });
      /* Long enough that the writes belonging to one customer action
         collapse into a single card refresh. Adding a product that crosses
         a threshold is two cart writes, the product and the gift, and at
         80ms the row was rebuilt twice with a different set of products
         each time. The row is not empty while we wait: it still shows the
         cards from the previous paint, which are still valid. */
    }, 400);
  };

  const refreshProgressModules = async () => {
    const requests = [];
    const root = window.Shopify?.routes?.root || '/';

    const replaceProgressModule = async (placement, sectionId) => {
      const current = document.querySelector(
        `${SELECTORS.module}[data-placement="${placement}"].ph-delivery-module--progress-only`
      );
      if (!current) return;

      const url = new URL(`${root}cart`, window.location.origin);
      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('_ph', String(Date.now()));

      const response = await fetch(url.toString(), {
        headers: { Accept: 'text/html' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error('Unable to refresh cart progress.');

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const updated = parsed.querySelector(
        `${SELECTORS.module}[data-placement="${placement}"].ph-delivery-module--progress-only`
      );

      /* Copy the data across instead of replacing the node.

         This used to be current.replaceWith(updated), which threw away the
         scroller and every card in it on each cart change, so the block
         went blank, collapsed to nothing, then grew back once the cards had
         been fetched again. Nothing in the server render is visible here:
         the module is an empty shell whose numbers live in data attributes
         and whose cards are filled in by renderModule. So take the
         attributes and leave the DOM alone. */
      if (!updated || !current.isConnected) return;

      Array.from(updated.attributes).forEach((attribute) => {
        if (attribute.name === 'class') return;
        if (current.getAttribute(attribute.name) !== attribute.value) {
          current.setAttribute(attribute.name, attribute.value);
        }
      });

      /* Classes carry render state (is-loading, is-empty) as well as the
         server's own, so merge rather than overwrite. */
      updated.classList.forEach((name) => {
        if (name.startsWith('is-')) return;
        current.classList.add(name);
      });
    };

    if (document.querySelector('m-cart-drawer')) {
      requests.push(replaceProgressModule('drawer', 'cart-drawer'));
    }
    if (document.querySelector('m-cart')) {
      requests.push(replaceProgressModule('page', 'cart-template'));
    }

    await Promise.all(requests);
    await refreshAll();
  };

  /* The module's numbers arrive with the theme's own re-render.

     Both cart surfaces re-render the container this module sits in on every
     cart change, and the reward bar has its own registered region now, so
     the data attributes on screen are already current by the time this
     runs. Fetching the section again to read the same values was a second
     network round trip and a second repaint per change: the recommendations
     refreshing twice. All that is still needed is the card refresh. */
  const scheduleProgressRefresh = () => {
    refreshAll();
  };

  const scheduleProgressRefreshFromServer = () => {
    window.clearTimeout(state.progressRefreshTimer);
    state.progressRefreshTimer = window.setTimeout(async () => {
      if (state.progressRefreshInFlight) {
        await state.progressRefreshInFlight;
      }

      state.progressRefreshInFlight = refreshProgressModules()
        .catch((error) => {
          console.error('Prohealth cart progress:', error);
          refreshAll();
        })
        .finally(() => {
          state.progressRefreshInFlight = null;
        });

      await state.progressRefreshInFlight;
    }, 90);
  };

  const checkCartChange = async ({ forceProgress = false } = {}) => {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const cart = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      }).then((response) => response.json());

      const itemSignature = cart.items
        .map((item) => `${item.key}:${item.quantity}:${item.final_line_price}`)
        .join('|');
      const signature = `${cart.total_price}|${cart.item_count}|${itemSignature}`;
      const hadSignature = Boolean(state.cartSignature);
      const changed = hadSignature && signature !== state.cartSignature;

      state.cartSignature = signature;
      sessionStorage.setItem('phCartSignature', signature);

      if (changed || forceProgress) {
        scheduleProgressRefresh();
      } else {
        refreshAll({ rotate: changed });
      }
    } catch (error) {
      scheduleProgressRefresh();
    }
  };

  const addProduct = async (button) => {
    if (button.classList.contains('is-loading')) return;

    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    const module = button.closest(SELECTORS.module);
    const deliveryWasComplete = Number(module?.dataset.remaining || 0) <= 0;
    const scrollTarget = deliveryWasComplete ? '[data-ph-unlock-gifts]' : '[data-ph-delivery-module]';

    const originalText = button.innerHTML;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Unable to add this product.');
      }

      state.rotation += 1;
      sessionStorage.setItem('phUpsellRotation', String(state.rotation));

      /* The drawer is repainted by hand because nothing subscribes
         m-cart-drawer to the cart event. The cart page is not: m-cart
         subscribes and repaints itself from the emit below, so calling
         onCartUpdate here as well fetched and redrew the page twice. Same
         split as refreshThemeCart in prohealth-cart-unlocked-gifts.js. */
      const drawer = document.querySelector('m-cart-drawer');
      if (drawer && typeof drawer.onCartDrawerUpdate === 'function') {
        drawer.onCartDrawerUpdate(true);
      }

      if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
        window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, {
          source: 'prohealth-cart-upsell'
        });
      } else {
        const cartPage = document.querySelector('m-cart');
        if (cartPage && typeof cartPage.onCartUpdate === 'function') {
          cartPage.onCartUpdate(true);
        }
      }

      window.setTimeout(checkCartChange, 450);
      window.setTimeout(() => scrollToElementWhenReady(scrollTarget), 320);
    } catch (error) {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
      button.innerHTML = originalText;

      if (window.MinimogTheme?.Notification?.show) {
        window.MinimogTheme.Notification.show({
          target: button.closest(SELECTORS.card),
          type: 'warning',
          message: error.message
        });
      } else {
        console.error(error);
      }
    }
  };

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const continueLink = event.target.closest('[data-ph-continue-shopping]');
    if (!continueLink) return;
    event.preventDefault();
    continueLink.click();
  });

  document.addEventListener('click', (event) => {
    const continueLink = event.target.closest('[data-ph-continue-shopping]');
    if (continueLink) {
      event.preventDefault();

      const richTextLink = event.target.closest('a[href]');
      const customHref = richTextLink?.href;

      if (customHref) {
        if (richTextLink.target === '_blank') {
          window.open(customHref, '_blank', 'noopener');
        } else {
          window.location.assign(customHref);
        }
        return;
      }

      if (continueLink.dataset.placement === 'drawer' && !isCartPath(window.location.pathname)) {
        const drawer = continueLink.closest('m-cart-drawer') || document.querySelector('m-cart-drawer');
        const closeButton = drawer?.querySelector('.m-cart-drawer__close');
        if (closeButton) closeButton.click();
        else if (typeof drawer?.close === 'function') drawer.close();
        return;
      }

      window.location.assign(getContinueShoppingUrl(continueLink.dataset.fallbackUrl));
      return;
    }

    const button = event.target.closest('[data-ph-upsell-add]');
    if (!button) return;
    event.preventDefault();
    addProduct(button);
  });

  /* Fill a freshly rendered row before the browser paints it.

     A cart change re-renders the drawer from Liquid and the cards are
     client side, so the new row arrives empty. Waiting for the debounced
     refresh to put cards back left the row blank for about a second. This
     runs inside the same MutationObserver callback that notices the new
     module, which is before the next paint, so the previous cards are
     already there. They are replaced by the real ones a moment later. */
  const restoreCardsImmediately = () => {
    if (!state.lastCardsHtml) return;

    document.querySelectorAll(SELECTORS.module).forEach((module) => {
      const upsell = module.querySelector(SELECTORS.upsell);
      const list = module.querySelector(SELECTORS.list);
      if (!upsell || !list || list.children.length) return;

      list.innerHTML = state.lastCardsHtml;
      upsell.classList.remove('is-empty');
    });
  };

  const observer = new MutationObserver((mutations) => {
    const cartChanged = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some((node) =>
        node.nodeType === 1 &&
        (node.matches?.(SELECTORS.module) || node.querySelector?.(SELECTORS.module))
      )
    );
    if (!cartChanged) return;

    restoreCardsImmediately();
    checkCartChange();
  });

  document.addEventListener('DOMContentLoaded', () => {
    observer.observe(document.body, { childList: true, subtree: true });
    checkCartChange();
  });

  window.setTimeout(() => {
    if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
      window.MinimogEvents.subscribe(
        window.MinimogTheme.pubSubEvents.cartUpdate,
        () => window.setTimeout(() => checkCartChange({ forceProgress: true }), 250)
      );
    }
  }, 0);

  window.ProhealthCartDelivery = { refresh: checkCartChange, refreshProgress: refreshProgressModules };
})();
